import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import VideoCard from "@/components/feature/VideoCard";
import NotFound from "@/pages/NotFound";
import { getSpecialById, specials, videos } from "@/mocks/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SpecialPage() {
  const { specialId } = useParams();
  const special = specialId ? getSpecialById(specialId) : undefined;

  usePageMeta(
    special ? `${special.title} - 亲嘴视频专题` : "亲嘴视频 - 精选亲吻视频合集在线播放",
    special ? `${special.title}专题合集：${special.intro}` : "亲嘴视频专题合集。"
  );

  if (!special) return <NotFound />;

  const specialVideos = special.videoIds
    .map((id) => videos.find((v) => v.id === id))
    .filter((v): v is (typeof videos)[number] => Boolean(v));

  const otherSpecials = specials.filter((s) => s.id !== special.id);

  return (
    <SiteLayout hasTopPadding={false}>
      <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden bg-foreground-950">
        <img
          src={special.cover}
          alt={`${special.title} - 亲吻视频专题主图`}
          title={`${special.title} - 亲嘴视频专题`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/50 to-foreground-950/40" />
        <div className="relative z-10 w-full h-full flex items-end">
          <div className="w-full px-4 md:px-8 pb-10 md:pb-14 max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-label">
                专题合集
              </span>
              <span className="px-3 py-1 rounded-full bg-background-50/15 text-background-100 text-xs font-label backdrop-blur">
                共 {specialVideos.length} 部作品
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl text-background-50 mb-4">
              {special.title}
            </h1>
            <p className="text-sm md:text-base text-background-200/90 leading-relaxed">
              {special.intro}
            </p>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span>专题</span>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">{special.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-8">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">
              合集内容
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {specialVideos.map((video, index) => (
                <VideoCard key={video.id} video={video} index={index} />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6 lg:sticky lg:top-24">
              <h2 className="font-heading text-lg text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-line text-accent-500" />
                推荐理由
              </h2>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {special.reason}
              </p>
              <div className="mt-6 pt-5 border-t border-background-200/70">
                <h3 className="text-sm font-medium text-foreground-950 mb-3">
                  相关专题
                </h3>
                <div className="space-y-3">
                  {otherSpecials.map((s) => (
                    <Link
                      key={s.id}
                      to={`/special/${s.id}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <div className="relative w-24 shrink-0 aspect-video rounded-md overflow-hidden bg-background-200">
                        <img
                          src={s.cover}
                          alt={`${s.title} - 相关亲吻视频专题`}
                          title={`${s.title} - 亲嘴视频专题`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-950 font-medium line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {s.title}
                        </p>
                        <p className="text-xs text-foreground-500 mt-1 line-clamp-2 leading-relaxed">
                          {s.intro}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}